// [2026-09-27] ADDED — remember which product the person picked, in the cookie every other
// InCruiter app reads, BEFORE leaving for it.
//
// THE BUG. The account app decides WHICH product to draw at /dashboard from one shared cookie,
// `iCpD_<env>` (frontend-account src/RouteList/RoutesFile.jsx:52-53, container/MainPage.jsx:264,
// Layout/Sidebar.jsx:118). IncFeed and IncVid both live on that one host, so the cookie is the only
// thing that tells them apart. Every other sidebar writes it on click and only then navigates —
// frontend-account, frontend-incbot and frontend-increcruit, all three in
// src/component/Layout/Sidebar.jsx navigateToDashboard(). InC-Proctor navigated WITHOUT writing it,
// so the account app drew whatever was saved last: click IncFeed, get IncVid; or, when the last
// product saved was InC-Proctor itself, the account app treated the visit as a bounce and showed
// the product picker (MainPage.jsx:303-356).
//
// THIS IS A PORT, NOT A DESIGN. Same cookie name, same JSON fields in the same order, same
// encryption, same cookie options as the account app's localStorageEncrypt() +
// getSecureCookieOptions() (frontend-account src/utils/helper.jsx:389-413).
//
// WHY NOT `npm install crypto-js` LIKE THEY DO. proctoring-frontend-admin builds against this
// package, and its package-lock.json records THIS package's dependency list
// (proctoring-frontend-admin/package-lock.json:28). A new dependency here makes the admin image's
// `npm ci` fail until a second repository is changed and deployed in step. So the exact bytes
// crypto-js produces are produced here with the browser's own AES (Web Crypto) — no package.
//
// WHAT crypto-js DOES, verified in its source (4.2.0, the version installed in frontend-account and
// frontend-incbot): CryptoJS.AES.encrypt(text, passphrase).toString() is OpenSSL's "Salted__"
// format — an 8-byte random salt; key and IV from EVP_BytesToKey with MD5 and 1 iteration
// (evpkdf.js cfg, cipher-core.js OpenSSLKdf); AES-256-CBC with PKCS#7 padding (aes.js keySize
// 256/32, cipher-core.js mode CBC / padding Pkcs7); then base64("Salted__" + salt + ciphertext).
// Web Crypto has AES-CBC (PKCS#7 is its only padding) but not MD5, so MD5 is the one piece written
// out below.
//
// FAIL-OPEN: any failure here (no Web Crypto, cookies blocked) is the caller's to swallow; the click
// must still go where it went before this file existed.
import { envCookieSuffix } from './productLinks.js'

// Not a secret: the same literal ships in every InCruiter frontend's JavaScript bundle
// (frontend-account src/utils/helper.jsx:13, frontend-incbot src/utils/helper.js:12,
// frontend-increcruit src/utils/helper.js:12). It must equal theirs or they cannot read the cookie.
const SHARED_ENCRYPT_KEY = 'y347t567t347InCrUiTeRb6t5b3487y5'

// ── MD5 (RFC 1321) ─────────────────────────────────────────────────────────────────────────
// Only used to derive the AES key/IV the way crypto-js does. Not used for anything else.
const MD5_S = [
  7, 12, 17, 22, 7, 12, 17, 22, 7, 12, 17, 22, 7, 12, 17, 22,
  5, 9, 14, 20, 5, 9, 14, 20, 5, 9, 14, 20, 5, 9, 14, 20,
  4, 11, 16, 23, 4, 11, 16, 23, 4, 11, 16, 23, 4, 11, 16, 23,
  6, 10, 15, 21, 6, 10, 15, 21, 6, 10, 15, 21, 6, 10, 15, 21,
]
// The same table crypto-js builds in md5.js: floor(|sin(i + 1)| * 2^32).
const MD5_K = Array.from({ length: 64 }, (_, i) => (Math.abs(Math.sin(i + 1)) * 0x100000000) >>> 0)

export function md5(bytes) {
  const len = bytes.length
  const blocks = ((len + 8) >>> 6) + 1
  const w = new Uint32Array(blocks * 16)
  for (let i = 0; i < len; i++) w[i >> 2] |= bytes[i] << ((i % 4) * 8)
  w[len >> 2] |= 0x80 << ((len % 4) * 8)
  w[blocks * 16 - 2] = (len * 8) >>> 0
  w[blocks * 16 - 1] = Math.floor(len / 0x20000000)

  let a0 = 0x67452301
  let b0 = 0xefcdab89
  let c0 = 0x98badcfe
  let d0 = 0x10325476
  for (let off = 0; off < w.length; off += 16) {
    let a = a0
    let b = b0
    let c = c0
    let d = d0
    for (let i = 0; i < 64; i++) {
      let f
      let g
      if (i < 16) { f = (b & c) | (~b & d); g = i }
      else if (i < 32) { f = (d & b) | (~d & c); g = (5 * i + 1) % 16 }
      else if (i < 48) { f = b ^ c ^ d; g = (3 * i + 5) % 16 }
      else { f = c ^ (b | ~d); g = (7 * i) % 16 }
      const sum = (a + f + MD5_K[i] + w[off + g]) >>> 0
      a = d
      d = c
      c = b
      b = (b + ((sum << MD5_S[i]) | (sum >>> (32 - MD5_S[i])))) >>> 0
    }
    a0 = (a0 + a) >>> 0
    b0 = (b0 + b) >>> 0
    c0 = (c0 + c) >>> 0
    d0 = (d0 + d) >>> 0
  }

  const out = new Uint8Array(16)
  ;[a0, b0, c0, d0].forEach((word, j) => {
    for (let k = 0; k < 4; k++) out[j * 4 + k] = (word >>> (k * 8)) & 0xff
  })
  return out
}

function concatBytes(...parts) {
  const out = new Uint8Array(parts.reduce((n, p) => n + p.length, 0))
  let at = 0
  for (const p of parts) { out.set(p, at); at += p.length }
  return out
}

/** CryptoJS.AES.encrypt(text, passphrase).toString(), byte for byte (given the same salt). */
export async function cryptoJsAesEncrypt(text, passphrase, salt = crypto.getRandomValues(new Uint8Array(8))) {
  const enc = new TextEncoder()
  const pass = enc.encode(passphrase)

  // EVP_BytesToKey, MD5, 1 iteration: D1 = MD5(pass+salt), Di = MD5(D(i-1)+pass+salt), until 48
  // bytes — 32 of key, 16 of IV (crypto-js evpkdf.js compute(), cipher-core.js OpenSSLKdf).
  let derived = new Uint8Array(0)
  let block = new Uint8Array(0)
  while (derived.length < 48) {
    block = md5(concatBytes(block, pass, salt))
    derived = concatBytes(derived, block)
  }

  const key = await crypto.subtle.importKey('raw', derived.slice(0, 32), { name: 'AES-CBC' }, false, ['encrypt'])
  const cipher = new Uint8Array(await crypto.subtle.encrypt(
    { name: 'AES-CBC', iv: derived.slice(32, 48) }, key, enc.encode(text)))

  const all = concatBytes(enc.encode('Salted__'), salt, cipher)
  let binary = ''
  for (let i = 0; i < all.length; i++) binary += String.fromCharCode(all[i])
  return btoa(binary)
}

/** The account app's `localStorageEncrypt('productId', …)`, as its sidebar calls it on a product
 *  click (frontend-account src/component/Layout/Sidebar.jsx navigateToDashboard). */
export async function rememberSelectedProduct(product) {
  const value = await cryptoJsAesEncrypt(JSON.stringify({
    // `/me/products` sends the product's Mongo _id as `id` (proctoring-sdk-backend
    // app/modules/me/route.py my_products); the account app's own list calls it `_id`.
    productId: product?._id || product?.id,
    isActive: product?.status,
    productSubDomain: product?.subdomain,
    // Not sent by /me/products, so absent here, and JSON.stringify drops it. Safe: the only reader
    // (Credit.jsx → /plan-pricing → PlanCard) never uses it.
    productFeatures: product?.productHighlights,
    productName: product?.name,
  }), SHARED_ENCRYPT_KEY)

  // getSecureCookieOptions(): domain incruiter.com (localhost locally), path /, Secure except on
  // localhost, SameSite=Strict, 1 day. js-cookie writes a base64 value unescaped, and so does this.
  const isLocalhost = window.location.hostname === 'localhost'
  const expires = new Date(Date.now() + 864e5).toUTCString()
  document.cookie = [
    `iCpD_${envCookieSuffix()}=${value}`,
    'path=/',
    `domain=${isLocalhost ? 'localhost' : 'incruiter.com'}`,
    ...(isLocalhost ? [] : ['secure']),
    'sameSite=Strict',
    `expires=${expires}`,
  ].join('; ')
}
